// src/features/leases/components/FinancialTermsCard.jsx
//
// LSE-TAB-REDESIGN Part 2 — financial summary card.
//
// LSE-UNIFIED-EDIT-UX-PHASE-2 Sub-phase 1d: all inline-edit affordances
// removed. canWrite / onSaved props dropped. Edits go through EditLeaseModal.
// EditableLeaseField retained as pure read-only display (canWrite={false}).
//
// LSE-LEASE-TAB-FINANCIAL-FALLBACK: renders EstimatedValue (tooltip)
// when computed.isDerived is true (annual rent derived from total_cost).
// LSE-PHOTO-CARD-DERIVED-VALUE-INDICATOR-SIMPLIFY: italic removed; tooltip
// is now the sole indicator of derivation.
//
// LEASE-TERMINOLOGY-STANDARDIZE: labels use Contractual / Net vocabulary.

import React from "react";
import { EditableLeaseField } from "./EditableLeaseField.jsx";
import { EstimatedValue } from "./EstimatedValue.jsx";
import { sourceFor } from "../LeaseSummaryTab.jsx";
import { T } from "../../../app/tokens.js";
import { fmtMoney } from "../../../utils/format.js";
import { convertAmount } from "../../../utils/fxConvert.js";
import { PaneHeading } from "../../../components/PaneHeading.jsx";
import { Tooltip } from "../../../components/Tooltip.jsx";
import { useHoverIntent } from "../../../components/useHoverIntent.js";
import { AnnualRentBreakdown, hasRecoveryBasis, netRentDisplay } from "./AnnualRentBreakdown.jsx";
import { leaseAnnualHeadlines } from "../../../shared/leaseAnnualHeadlines.js";
import { AreaRentCurrentRates } from "./AreaRentCurrentRates.jsx";
import { MonthlyRentBasis } from "./MonthlyRentBasis.jsx";

const CARD_STYLE = {
  background: T.card,
  border: `1px solid ${T.border}`,
  borderRadius: 6,
  padding: "11px 13px",
};

const TITLE_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 9,
  marginBottom: 10,
};

const PILL_STYLE = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 6,
  fontSize: T.fsXxxs,
  fontWeight: T.fwSemibold,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  background: T.lightGray,
  color: T.mid,
};

const RUN_RATE_MSG = "Stabilized full year - no abatement";

function sumScheduleRows(rows, field, targetCurrency, rates) {
  let total = 0;
  for (const row of rows) {
    const rowCurrency = row.currency || targetCurrency;
    const converted = convertAmount(row[field], rowCurrency, targetCurrency, rates);
    if (converted == null) return null;
    total += converted;
  }
  return total;
}

const CONTRACTUAL_DEF = "Recorded current-calendar-year base rent before free-rent abatement. Partial years retain their actual totals; no 12-month scaling.";
const NET_DEF         = "Contractual minus free-rent abatement - the amount actually billed that year.";

// Lightweight inline definition tooltip — renders label text + ⓘ with hover popup.
// Uses the same hover-zone/popup pattern as EstimatedValue; no new dependency.
function DefLabel({ text, tip }) {
  const { open, triggerProps, popupProps } = useHoverIntent({ interactive: true });
  return (
    <span
      data-testid="deflabel-hover-zone"
      style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 3, cursor: "help" }}
      {...triggerProps}
      tabIndex={0}
    >
      {text}
      <span
        aria-hidden="true"
        style={{ cursor: "help", color: T.mid, fontSize: T.fsXs, userSelect: "none", lineHeight: 1 }}
      >
        ⓘ
      </span>
      {open && (
        <Tooltip
          {...popupProps}
          data-testid="deflabel-tooltip-popup"
          style={{
            bottom: "calc(100% + 4px)",
            left: 0,
            zIndex: 100,
            whiteSpace: "normal",
            width: 240,
          }}
        >
          {tip}
        </Tooltip>
      )}
    </span>
  );
}

export function FinancialTermsCard({ lease, computed, displayCcy, rates, schedulePeriod, drawer = false }) {
  const nativeCcy = lease.currency || "USD";
  const effectiveDisplay = displayCcy || nativeCcy;

  const sameCcy = effectiveDisplay === nativeCcy;
  const canConvert =
    sameCcy ||
    (rates != null && convertAmount(1, nativeCcy, effectiveDisplay, rates) != null);
  const usedCurrency = canConvert ? effectiveDisplay : nativeCcy;

  const base = {
    locationId: lease.locationId,
    leaseId: lease.id,
    lease,
    canWrite: false,
  };

  // ── Provenance source helpers (LSE-PROVENANCE-BADGE-DERIVED-INHERITS-TOTAL-COST) ──
  // When isDerived=true, the KPI values are estimated from total_cost ÷ term.
  // The M precedence rule (#53) requires the badge to follow total_cost's provenance
  // rather than the (unpopulated) per-KPI fieldSources keys.
  const isDerived = !!computed?.isDerived;
  const totalCostSource = lease.fieldSources?.totalCost || "spacebase";

  function derivedKpiSource(field) {
    if (!isDerived) return lease.fieldSources?.[field] || "spacebase";
    return totalCostSource;
  }

  function rentPerSqftSource() {
    if (!isDerived) return "computed";
    // M precedence rule: manually-driven derived data overrides "combination" semantics.
    return totalCostSource === "manual" ? "manual" : "computed";
  }

  function toUsed(nativeAmount, sourceCurrency = nativeCcy) {
    if (nativeAmount == null) return null;
    if (sourceCurrency === usedCurrency) return Number(nativeAmount);
    return convertAmount(nativeAmount, sourceCurrency, usedCurrency, rates);
  }

  function renderMoney(nativeAmount, sourceCurrency = nativeCcy) {
    const converted = drawer ? convertAmount(nativeAmount, sourceCurrency, effectiveDisplay, rates) : toUsed(nativeAmount, sourceCurrency);
    const formatted = fmtMoney(drawer ? converted ?? nativeAmount : converted, drawer ? converted == null ? sourceCurrency : effectiveDisplay : usedCurrency);
    const derived = !!computed?.isDerived;
    return (
      <span style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>
        {derived ? <EstimatedValue value={formatted} /> : formatted}
      </span>
    );
  }

  function renderMoneyEstimated(nativeAmount, isEstimated, sourceCurrency = nativeCcy) {
    const converted = drawer ? convertAmount(nativeAmount, sourceCurrency, effectiveDisplay, rates) : toUsed(nativeAmount, sourceCurrency);
    const formatted = fmtMoney(drawer ? converted ?? nativeAmount : converted, drawer ? converted == null ? sourceCurrency : effectiveDisplay : usedCurrency);
    return (
      <span style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>
        {isEstimated
          ? <EstimatedValue
              value={<em style={{ fontStyle: "italic" }}>{formatted}</em>}
              message={RUN_RATE_MSG}
            />
          : formatted}
      </span>
    );
  }

  function renderPerSqftEstimated(nativeAmount, isEstimated, sourceCurrency = nativeCcy) {
    const formatted = fmtMoney(toUsed(nativeAmount, sourceCurrency), usedCurrency, { decimals: drawer ? 2 : 0 });
    return (
      <span style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>
        {isEstimated
          ? <EstimatedValue
              value={<em style={{ fontStyle: "italic" }}>{formatted}</em>}
              message={RUN_RATE_MSG}
            />
          : formatted}
        {" / sqft"}
      </span>
    );
  }

  function renderPerSqft(nativeAmount, sourceCurrency = nativeCcy) {
    const formatted = fmtMoney(toUsed(nativeAmount, sourceCurrency), usedCurrency, { decimals: drawer ? 2 : 0 });
    const derived = !!computed?.isDerived;
    return (
      <span style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>
        {derived ? <EstimatedValue value={formatted} /> : formatted}
        {" / sqft"}
      </span>
    );
  }

  const year = new Date().getFullYear();
  const scheduleRows = (lease?.rentSchedule || []).filter(
    (row) => !row.isProjected && !row.isFallback
  );
  const hasScheduleValues = scheduleRows.length > 0;
  const headlines = leaseAnnualHeadlines(lease, computed, year);
  const recoveryBasis = headlines.basis !== "monthly_actual" && hasRecoveryBasis(lease);
  const currentNet = headlines.net;
  const currentRunRate = headlines.contractual;
  const lifetimeInDisplayCurrency = hasScheduleValues
    ? sumScheduleRows(scheduleRows, "net", usedCurrency, rates)
    : null;
  const billedPerSqft = headlines.netPsf;

  // Free rent: render "Not in this lease" (italic, muted) when absent/zero.
  const freeRentValue = lease.freeRentMonths != null ? Number(lease.freeRentMonths) : null;
  const hasFreeRent   = freeRentValue != null && freeRentValue > 0;

  const freeRentRow = hasFreeRent ? (
    <EditableLeaseField {...base}
      field="freeRentMonths" label="Free rent" value={lease.freeRentMonths}
      type="number" suffix="months"
      source={sourceFor(lease, "freeRentMonths")} />
  ) : (
    <div
      data-testid="lease-field-freeRentMonths"
      style={{
        display:        "flex",
        alignItems:     "center",
        justifyContent: "space-between",
        gap:            12,
        padding:        "9px 0",
        borderBottom:   `1px solid ${T.border}`,
      }}
    >
      <span
        data-testid="lease-field-label-freeRentMonths"
        style={{ fontSize: T.fsBase, color: T.mid, fontWeight: T.fwRegular, flexShrink: 0 }}
      >
        Free rent
      </span>
      <span
        data-testid="lease-field-value-freeRentMonths"
        style={{ fontSize: T.fsBase, color: T.light, fontStyle: "italic" }}
      >
        Not in this lease
      </span>
    </div>
  );

  return (
    <section data-testid="financial-terms-card" data-display-currency={usedCurrency} style={CARD_STYLE}>
      <div style={TITLE_STYLE}>
        <PaneHeading style={{ marginBottom: 0 }}>Financial terms</PaneHeading>
        <span data-testid="financial-terms-currency-pill" style={PILL_STYLE}>{usedCurrency}</span>
      </div>

      {/* Two-column grid: left = rent KPIs, right = cost / terms.
          Per LSE-PROVENANCE-BADGE-SEMANTICS (#53): monthlyRent, annualRent, and
          totalLifetimeCost are same-measure transformations (S badge).
          Per LSE-PROVENANCE-BADGE-DERIVED-INHERITS-TOTAL-COST: when isDerived=true,
          follow total_cost's provenance — M propagates from total_cost to all derived KPIs.
          LEASE-COST-DUAL-S1: annualRent split into billed (current year) + run-rate (stabilised).
          LEASE-TERMINOLOGY-STANDARDIZE: Contractual / Net vocabulary. */}
      <div
        data-testid="financial-terms-columns"
        style={{
          display:             "grid",
          gridTemplateColumns: "1fr 1fr",
          gap:                 "0 20px",
        }}
      >
        {/* Left column: rent KPIs */}
        <div>
          <EditableLeaseField {...base}
            field="monthlyRent" label="Monthly rent" emptyLabel={headlines.monthlyDisplay.basis === "not_in_source" ? "Not in source" : "Unavailable"}
            value={headlines.monthly}
            render={() => <>
              {headlines.monthlyDisplay.variable ? "Variable" : renderMoneyEstimated(
              headlines.monthly,
               false,
               headlines.monthlyDisplay.currency,
             )}
              <MonthlyRentBasis derivation={headlines.monthlyDisplay} />
            </>}
            source={headlines.monthlyDisplay.basis === "monthly_actual" ? "spacebase" : headlines.monthlyDisplay.approximate ? "computed" : derivedKpiSource("monthlyRent")} />
          <EditableLeaseField {...base}
            field="annualRentBilled"
            emptyLabel="Not in source"
            label={<DefLabel text={recoveryBasis ? `Net recorded /yr (${year})` : `Net /yr (${year})`} tip={headlines.basis === "monthly_actual" ? "Sum of signed sourced amounts payable, already net of abatement." : NET_DEF} />}
            value={recoveryBasis ? lease.annualRentUsd : currentNet}
            render={() => recoveryBasis ? <>
              {renderMoney(netRentDisplay(lease, displayCcy, rates).amount, netRentDisplay(lease, displayCcy, rates).currency)}
          {recoveryBasis && <AnnualRentBreakdown lease={lease} displayCcy={drawer ? displayCcy : undefined} rates={rates} />}
            </> : renderMoney(
              currentNet,
              headlines.currency,
            )}
            source={headlines.basis === "monthly_actual" ? "spacebase" : derivedKpiSource("annualRentBilled")} />
          <EditableLeaseField {...base}
            field="annualRentRunRate"
            emptyLabel="Not in source"
            label={<DefLabel text={`Contractual /yr (${year})`} tip={CONTRACTUAL_DEF} />}
            value={currentRunRate}
            render={() => renderMoneyEstimated(
              currentRunRate,
              false,
              headlines.contractualCurrency,
            )}
            source={derivedKpiSource("annualRentRunRate")} />
          <EditableLeaseField {...base}
            field="rentPerSqft"
            emptyLabel="Unavailable"
            label="Net / sqft"
            value={billedPerSqft}
            render={() => renderPerSqft(
              billedPerSqft,
              headlines.currency,
            )}
            source={rentPerSqftSource()} />
          <EditableLeaseField {...base}
            field="rentPerSqftRunRate"
            emptyLabel="Unavailable"
            label="Contractual / sqft"
            value={headlines.contractualPsf}
            render={() => renderPerSqftEstimated(
              headlines.contractualPsf,
              false,
              headlines.contractualCurrency,
            )}
            source={rentPerSqftSource()} />
        </div>

        {/* Right column: cost + lease terms */}
        <div>
          <EditableLeaseField {...base}
            field="totalLifetimeCost"
            label={schedulePeriod ? `Total (${schedulePeriod})` : "Total lifetime cost"}
            value={hasScheduleValues
              ? lifetimeInDisplayCurrency
              : computed?.totalLifetimeCost}
            render={() => renderMoney(
              hasScheduleValues ? lifetimeInDisplayCurrency : computed?.totalLifetimeCost,
              hasScheduleValues ? usedCurrency : nativeCcy,
            )}
            source={derivedKpiSource("totalLifetimeCost")} />
          {/* LEASE-NEW-FIELDS-DISPLAY: rentPaymentType promoted from accordion to main card */}
          {lease.rentPaymentType && (
            <EditableLeaseField {...base}
              field="rentPaymentType" label="Rent payment type" value={lease.rentPaymentType}
              source={sourceFor(lease, "rentPaymentType")} />
          )}
          <EditableLeaseField {...base}
            field="escalationType" label="Escalation" value={lease.escalationType}
            type="select" options={["Fixed", "CPI", "None"]}
            source={sourceFor(lease, "escalationType")} />
          <EditableLeaseField {...base}
            field="opexStructure" label="OpEx structure" value={lease.opexStructure}
            type="select" options={["NNN", "Modified Gross", "Full Service Gross", "Gross"]}
            source={sourceFor(lease, "opexStructure")} />
          {freeRentRow}
        </div>
      </div>
      <AreaRentCurrentRates lease={lease} displayCurrency={usedCurrency} rates={rates} drawer={drawer} />
    </section>
  );
}
